/**
 * État composé de l'arrêt d'urgence de l'IA (§ 4.10.1), lu sur `GET /ai/kill-switch/state`
 * (FND-U6B-01, clos) : la passerelle compose la portée plateforme et celle de l'organisation du
 * jeton, et la sert à TOUT membre authentifié — lecteur compris. Motif et auteur n'y figurent
 * que pour l'administrateur plateforme et l'owner de l'organisation.
 *
 * Filet conservé : si la lecture échoue (passerelle antérieure, panne), le bandeau s'affiche
 * encore après un refus constaté (`AI_KILL_SWITCH_ACTIVE`).
 */
import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { api } from '@/api/client'
import type { AiKillSwitchScopeState } from '@/api/types/aiAdmin'
import { EVENEMENT_ARRET_CONSTATE, composerCauses, type CauseArret, type EtatPortee } from '@/lib/arretIA'

/** Délai de relecture : AC-PF4 veut le bandeau sur toutes les routes en moins de 5 s. */
export const RELECTURE_MS = 5_000
/** Un refus constaté (sans lecture possible de la cause) reste signalé 5 min après le dernier. */
export const DUREE_CONSTAT_MS = 5 * 60_000

export const CLES_ARRET = {
  etat: ['arret-ia', 'etat'] as const,
  plateforme: ['arret-ia', 'plateforme'] as const,
  organisation: ['arret-ia', 'organisation'] as const,
  historiqueOrganisation: ['arret-ia', 'historique-organisation'] as const,
  historiquePlateforme: ['arret-ia', 'historique-plateforme'] as const,
}

export interface EtatArretIA {
  causes: CauseArret[]
  /** L'état composé a été lu. */
  lisible: boolean
  /** Instant du dernier refus `AI_KILL_SWITCH_ACTIVE` constaté par ce navigateur. */
  constate: Date | null
  superAdmin: boolean
  administrateurOrganisation: boolean
  plateforme: EtatPortee | undefined
  organisation: EtatPortee | undefined
}

/** Portée de l'état composé → forme commune aux règles pures. */
export function versEtatPortee(p: AiKillSwitchScopeState | undefined): EtatPortee | undefined {
  if (!p) return undefined
  return { enabled: p.actif, updated_at: p.depuis, reason: p.motif ?? null, updated_by: p.par ?? null }
}

export function useEtatArretIA(roles: readonly string[]): EtatArretIA {
  const superAdmin = roles.includes('admin')
  const administrateurOrganisation = superAdmin || roles.includes('workspace_owner')

  const etat = useQuery({
    queryKey: CLES_ARRET.etat,
    queryFn: api.getAiKillSwitchState,
    refetchInterval: RELECTURE_MS,
    retry: false,
  })

  const [constate, setConstate] = useState<Date | null>(null)
  useEffect(() => {
    let minuterie: ReturnType<typeof setTimeout> | undefined
    const surRefus = (): void => {
      setConstate(new Date())
      if (minuterie) clearTimeout(minuterie)
      minuterie = setTimeout(() => setConstate(null), DUREE_CONSTAT_MS)
      void etat.refetch()
    }
    window.addEventListener(EVENEMENT_ARRET_CONSTATE, surRefus)
    return () => {
      window.removeEventListener(EVENEMENT_ARRET_CONSTATE, surRefus)
      if (minuterie) clearTimeout(minuterie)
    }
    // L'objet de requête change à chaque rendu ; son `refetch` est stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const plateforme = versEtatPortee(etat.data?.plateforme)
  const organisation = versEtatPortee(etat.data?.organisation)

  return {
    causes: composerCauses({ plateforme, organisation }),
    lisible: etat.isSuccess,
    constate,
    superAdmin,
    administrateurOrganisation,
    plateforme,
    organisation,
  }
}
